/**
 * StackMat.tsx - Stacked Spices Shader Material (Pre-Shake State)
 *
 * Custom shader material that renders spices as visible stacked layers inside the bottle.
 * Uses vertex displacement to deform geometry based on accumulated spice quantities,
 * and fragment masking to blend texture layers with soft organic edges.
 *
 * Vertex Shader:
 * - Clamps vertex Y positions to accumulated spice height (uAccQuantities)
 * - Applies Perlin noise for organic, uneven layer edges
 * - Creates dome effect on bottom hemisphere for realistic settling
 * - Recalculates UVs based on deformed height for correct texture mapping
 *
 * Fragment Shader:
 * - createMask(): Generates smoothstep masks for each spice layer
 * - Loops through active spices, applying masks and accumulating:
 *   - Diffuse color from albedo texture
 *   - Surface normals from normal maps
 *   - Roughness from alpha channel
 * - Falls back to low-res texture if high-res not yet loaded (uHighResMapsReady)
 *
 * Uniforms are managed externally by useUniforms hook and updated via useEffect
 * to prevent shader recompilation on uniform value changes.
 */

import CustomShaderMaterial from "three-custom-shader-material";
import { Box3, MeshStandardMaterial } from "three";
import { MaterialProp } from "../Spices";
import { MAX_SPICES } from "@/data/schema";
import { memo } from "react";
import { useControls, folder } from "leva";

export const StackMat = memo(function StackMat({
  uniforms,
  bbBox,
}: MaterialProp & { bbBox: Box3 }) {

  const controls = useControls("Spices", {
    mask: folder({
      scale: { value: 0.30, min: 0, max: 0.9, step: 0.001 },
      softEdge: { value: 0.02, min: 0, max: 0.1, step: 0.01 },
    },
    { collapsed: true }),
  });

  return (
    <CustomShaderMaterial
      baseMaterial={MeshStandardMaterial}
      
      /* glsl */
      vertexShader={`

        // constants
        #define MAX ${MAX_SPICES}
        const vec2 bbBox = vec2(${bbBox.min.y}, ${bbBox.max.y});
        const float cylinderRadius = ${Math.max(bbBox.max.x - bbBox.min.x, bbBox.max.z - bbBox.min.z) / 2.0};

        // activeCount
        uniform int uActiveCount;
        uniform float uQuantity[MAX];
        uniform float uAccQuantities;

        // noise
        uniform sampler2D uNoiseMap;
        uniform float uNoiseScale; 
        uniform float uNoiseAmp;
        uniform float uNoiseSeed; 

        // varyings
        varying vec2 vUv;
        varying float vMaskY;  // separate Y for mask calculation (decoupled from texture UVs)
        varying float vNoise;

        void main() {

          // Sample noise at 3 scales with UV offsets for decorrelated per-layer mask variation
          vec2 baseUv = position.xz * uNoiseScale;
          float seedOff = uNoiseSeed * 0.5;

          float n = texture(uNoiseMap, position.xz * uNoiseScale + seedOff).r * uNoiseAmp;
          float topEdge = bbBox.x + uAccQuantities * (bbBox.y - bbBox.x);
          vec3 modelPosition = position;
          modelPosition.y = clamp(position.y, bbBox.x, topEdge + n);

          // bottom hemisphere -> smoothly increase from the distance to the center -> but only for the bottom y the uAccQuantites are in the
          if (modelPosition.y <= (bbBox.x  * 0.05))                       // last 5 percent
          {
            float dist = length(position.xz);                             // distance from center
            modelPosition.y += smoothstep(0.0, 1.0, pow(dist, 2.3 + n));  // push upward depending on distance from center
          }

          if (uAccQuantities == 1.0 ) modelPosition.y = clamp(position.y, bbBox.x, topEdge);
          if (uAccQuantities == 0.0 ) modelPosition.y += 1000.0;

          csm_Position = modelPosition;

          // Calculate normalized uv for mask
          float normalizedY = (modelPosition.y - bbBox.x) / (bbBox.y - bbBox.x);
          float isTop = step(0.5, normal.y);
          vUv = uv;
          vUv = mix(vec2(uv.x, normalizedY), position.xz / (cylinderRadius * 5.0) + 0.5, isTop);
          vMaskY = mix(normalizedY, 1.0, isTop);
          vNoise = n; // reuse the noise to save compute
        }
      `}
      /* glsl */
      fragmentShader={`

        // constants
        #define MAX ${MAX_SPICES}

        // uniforms
        uniform int uActiveCount;
        uniform int uNeedsUpdate; 
        uniform float uQuantity[MAX];
        uniform int uActiveTextureSlots[MAX];
        uniform bool uHighResMapsReady[MAX];

        // soft edges
        uniform float uSoftEdge;

        // textures
        uniform sampler2DArray uTexture;
        uniform sampler2DArray uNormal;
        uniform sampler2DArray uFallback;

        // noise
        uniform float uNoiseAmp; // using this to prevent bleeding
        uniform float uMaskSeeds[MAX];  // per-layer mask noise randomness

        // props
        uniform float uTextureScale[MAX];
        uniform float uNorIntensity[MAX];
        uniform float uRoughnessIntensity[MAX];
        uniform float uAoIntensity[MAX];
        uniform float uColorIntensity[MAX];

        // varyings
        varying vec2 vUv;
        varying float vMaskY;  // separate Y for mask (decoupled from texture UVs)
        varying float vNoise;

        // functions
        float createMask(float p1, float p2, int i) {
          float prevScale = (i == 0) ? 1.0 : (0.8 + 0.4 * uMaskSeeds[i - 1]);
          float currScale = 0.8 + 0.4 * uMaskSeeds[i];
          float bottomY = vMaskY + vNoise * prevScale * float(${controls.scale});
          float topY    = vMaskY + vNoise * currScale * float(${controls.scale});
          return smoothstep(p1 - float(${controls.softEdge}), p1 + float(${controls.softEdge}), bottomY) * 
                 smoothstep(p2 + float(${controls.softEdge}), p2 - float(${controls.softEdge}), topY);
        }

        void main() {
          vec3 color = vec3(0.0);
          vec3 nor = vec3(0.0);
          float rough = 0.0;
          float ao = 0.0;
          float prevEnd = 0.0;

          for (int i = 0; i < uActiveCount; i++) {

            // check if the layers
            float start = prevEnd;
            float end = (i == uActiveCount - 1) ? 1.0 + uNoiseAmp : start + uQuantity[i];
            prevEnd = end;

            // layers above this fragment can't contribute — stop early
            if (start > vMaskY + float(${controls.softEdge}) + uNoiseAmp) break;

            // mask
            float maskValue = createMask(start, end, i);

            // skip texture fetches for layers that don't contribute to this fragment
            if (maskValue < 0.001) continue;

            // current slot
            int slot = uActiveTextureSlots[i];
            vec2 scaledUv = vUv * uTextureScale[i];

            // highRes
            if(uHighResMapsReady[i])
            {
              vec4 tex = texture(uTexture, vec3(scaledUv, slot));
              vec4 norMap = texture(uNormal, vec3(scaledUv, slot));
              norMap.rgb = norMap.rgb * 2.0 - 1.0;
              norMap.rgb = mix(vec3(0.0, 0.0, 1.0), norMap.rgb, uNorIntensity[i]);

              color   += tex.rgb * uColorIntensity[slot] * maskValue;
              //ao      += tex.a * uAoIntensity[slot] * maskValue; 
              nor     += norMap.rgb * maskValue;
              rough   += exp2(uRoughnessIntensity[i] * log2(norMap.a + 0.001)) * maskValue;
            }

            else
            {
              color   += texture(uFallback, vec3(scaledUv, float(slot))).rgb * uColorIntensity[slot] * maskValue;
              nor     += vec3(0.0, 0.0, 1.0) * maskValue;
              rough   += maskValue;
            }
        }

          csm_DiffuseColor  = vec4(color, 1.0);
          csm_FragNormal    = normalize(nor);
          csm_Roughness     = rough;
          csm_AO            = ao; 
        }

      `}
      uniforms={uniforms}
    />
  );
});

// Noise
// const noise = `
//   float hash(vec2 p) {
//       return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
//   }

//   float noise(vec2 p) {
//       vec2 i = floor(p);
//       vec2 f = fract(p);

//       float a = hash(i);
//       float b = hash(i + vec2(1.0, 0.0));
//       float c = hash(i + vec2(0.0, 1.0));
//       float d = hash(i + vec2(1.0, 1.0));

//       vec2 u = f * f * (3.0 - 2.0 * f);

//       return mix(a, b, u.x) +
//              (c - a) * u.y * (1.0 - u.x) +
//              (d - b) * u.x * u.y;
//   }
//   `;